import { PARTS, SLOTS, partById, starterOf, upCost, partMod } from '../data/parts';
import { META, saveMeta } from '../platform/storage';
import { P } from '../world/player';
import { registerModSource } from './stats';
/* ============================================================
   局外部件的持有、購買、裝備與生效（M10）
   ============================================================

   設計見 GDD §11.2。這個檔只管三件事：**誰擁有什麼、裝了什麼、效果怎麼套進去**。
   外形（每個部件長什麼樣）在 M11 接，資料上先留 `shape` 欄位。

   **效果分兩條路徑，跟被動同一套規則：**

     mods()   → 進修正層（`sim/stats.ts`），與被動一起算，**順序無關**
     grant()  → 不在 `P.s` 裡的東西（結構上限、復活、槽位、升級選項、主武裝改裝）

   為什麼不全部塞進 mods：槽位數與選項數不是「數值」而是「規則」，
   把它們放進乘加的修正層會讓「先算誰」變得有意義，那正是修正層要消滅的東西。

   **局外等級 0 的定義**：六個部位都裝起手件、且都是 Lv1。
   起手件 Lv1 一律沒有加成（見 `data/parts.ts` 的說明），
   所以這個狀態就是「新玩家的第一場」—— 全部校準測試都釘在這裡（GDD §11.4）。 */

/** 這一場生效的 grant 總和。開場算一次，之後不變。 */
export var GRANT = {
  hp: 0, sh: 0, revives: 0, dashCut: 0,
  wSlot: 0, pSlot: 0, choices: 0, freeLv: 0,
  /* 抽卡的能動性工具（`sim/draft.ts` 讀它們，基準是 Reroll 2 / Banish 1）。
     **供給端還沒接上** —— 目前沒有任何部件給這兩個，所以實際值恆為 0。
     欄位先留在這裡是為了讓 P4（局外成長排程）接上時不用回頭改抽卡層；
     `03-in-run-progression.md` 指定的取得方式就是局外強化與寶箱。 */
  rerolls: 0, banishes: 0,
  primaryMod: null as string | null
};

/* ── 每台機體各一套（使用者 2026-08-05 指定）─────────────────────
   `META.owned` / `META.equip` 的第一層 key 是機體 id（見 `platform/storage.ts`）。
   這個檔以下的每一個函式都以「**目前裝備中的那台**」為對象。

   為什麼不把 mechId 做成必填參數：呼叫點有二十幾處（UI、診斷、測試），
   全部要傳一個「其實永遠等於 P.mech.id」的值。做成選填、預設取當前機體，
   只有真的要跨機體查詢的地方（機庫的機體卡片）才顯式傳。 */
export var DEFAULT_MECH = 'chassis0';
/** 目前這台。`P.mech` 在載入存檔到第一次 equipMech 之間是空的，退回預設。 */
export function curMech() { return (P && P.mech && P.mech.id) || DEFAULT_MECH; }

function ownedMap(mechId?) {
  var id = mechId || curMech();
  if (!META.owned) META.owned = {} as any;
  if (!META.owned[id]) META.owned[id] = {};
  return META.owned[id];
}
function equipMap(mechId?) {
  var id = mechId || curMech();
  if (!META.equip) META.equip = {} as any;
  if (!META.equip[id]) META.equip[id] = {};
  return META.equip[id];
}

/** 確保這台機體有部件欄位，並把沒設定過的部位補上起手件。
    **新解鎖的機體第一次進機庫就會走這裡** —— 起手件成本 0，
    所以它一解鎖就是「局外等級 0」的可玩狀態，不是光溜溜的骨架。 */
export function ensureParts(mechId?) {
  if (typeof META.scrap !== 'number') META.scrap = 0;
  var own = ownedMap(mechId), eq = equipMap(mechId);
  for (var i = 0; i < SLOTS.length; i++) {
    var st = starterOf(SLOTS[i].id);
    if (!st) continue;
    if (!own[st.id]) own[st.id] = 1;
    if (!eq[SLOTS[i].id]) eq[SLOTS[i].id] = st.id;
  }
}

export function ownedLevel(id, mechId?) { return ownedMap(mechId)[id] || 0; }
export function equippedId(slot, mechId?) { return equipMap(mechId)[slot] || null; }

/** 買一個部件。回傳成功與否 —— 廢料不夠或已擁有都是 false。 */
export function buyPart(id) {
  var p = partById(id);
  if (!p || ownedLevel(id) > 0 || META.scrap < p.cost) return false;
  META.scrap -= p.cost;
  ownedMap()[id] = 1;
  saveMeta();
  return true;
}

/** 升一級。最高 5 級。 */
export function upgradePart(id) {
  var lv = ownedLevel(id);
  if (!lv || lv >= 5) return false;
  var cost = upCost(lv + 1);
  if (META.scrap < cost) return false;
  META.scrap -= cost;
  ownedMap()[id] = lv + 1;
  saveMeta();
  return true;
}

/** 裝上。**買了就能自由切換**，換部件不用重買（GDD §11.2）。 */
export function equipPart(id) {
  var p = partById(id);
  if (!p || ownedLevel(id) <= 0) return false;
  equipMap()[p.slot] = id;
  saveMeta();
  return true;
}

/** 這一場已裝備的部件清單（含等級）。 */
export function equippedParts() {
  ensureParts();
  var out: Array<{ part: any, lv: number }> = [];
  var eq = equipMap();
  for (var i = 0; i < SLOTS.length; i++) {
    var id = eq[SLOTS[i].id];
    var p = partById(id);
    if (p) out.push({ part: p, lv: ownedLevel(id) });
  }
  return out;
}

/** 開場呼叫一次：把 grant 類的效果結算成 GRANT。修正層由 registerModSource 自動吃。 */
export function applyParts() {
  GRANT.hp = 0; GRANT.sh = 0; GRANT.revives = 0; GRANT.dashCut = 0;
  GRANT.wSlot = 0; GRANT.pSlot = 0; GRANT.choices = 0; GRANT.freeLv = 0;
  GRANT.rerolls = 0; GRANT.banishes = 0;
  GRANT.primaryMod = null;
  var list = equippedParts();
  for (var i = 0; i < list.length; i++) {
    var g = list[i].part.grant ? list[i].part.grant(list[i].lv) : null;
    if (!g) continue;
    GRANT.hp += g.hp || 0;
    GRANT.sh += g.sh || 0;
    GRANT.revives += g.revives || 0;
    GRANT.dashCut += g.dashCut || 0;
    GRANT.wSlot += g.wSlot || 0;
    GRANT.pSlot += g.pSlot || 0;
    GRANT.choices += g.choices || 0;
    GRANT.freeLv += g.freeLv || 0;
    GRANT.rerolls += g.rerolls || 0;
    GRANT.banishes += g.banishes || 0;
    /* **不要直接用 `g.primaryMod`。** 那是以 CHASSIS-0 的武器寫的代號，
       近戰機體吃不到（`data/parts.ts` 的 MAIN_ALT 說明）。 */
    if (g.primaryMod) GRANT.primaryMod = partMod(list[i].part, P.mech ? P.mech.primary : 'vulcan');
  }
  /* 結構與護盾上限是**加在機體基準之上**，而且要跟著回滿 ——
     開局就帶著缺角的血條是個看起來像 bug 的東西。 */
  P.hpMax = P.mech.hp + GRANT.hp; P.hp = P.hpMax;
  P.shMax = P.mech.sh + GRANT.sh; P.sh = P.shMax;
  P.revives += GRANT.revives;
  /* 衝刺不再有格數（2026-08-06 併進推進能量），局外部件改成**減免耗能**，
     實際成本由 `sim/flight.ts` 的 `dashCost()` 算 —— 這裡不用做任何事。 */
  P.mod.primary = GRANT.primaryMod;
  P.pendingLv += GRANT.freeLv;
  return GRANT;
}

/** 一場結束的廢料收入。時間佔比高於擊殺 —— 撐下去比清場重要。 */
export function scrapReward(kills, seconds, won) {
  return Math.round(kills * 0.06 + seconds * 0.5 + (won ? 250 : 0));
}

/* 部件的修正丟進數值層，跟被動同一層。註冊一次就好。 */
registerModSource(function () {
  var out: any[] = [];
  var list = equippedParts();
  for (var i = 0; i < list.length; i++) {
    if (list[i].part.mods) out.push(list[i].part.mods(list[i].lv));
  }
  return out;
});

/** 給測試與診斷：把局外進度釘回「等級 0」（全部起手件 Lv1）。 */
export function resetPartsToBaseline() {
  META.owned = {} as any; META.equip = {} as any; META.scrap = 0;
  ensureParts();
}

/** 給診斷：全部部件買滿升滿，用來量「局外全滿」那一組對照（GDD §11.4）。 */
export function maxAllParts(mechId?) {
  var own = ownedMap(mechId);
  for (var k in own) delete own[k];
  for (var i = 0; i < PARTS.length; i++) own[PARTS[i].id] = 5;
  ensureParts(mechId);
}
